import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { X, Download, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface FilePreviewModalProps {
  file: File | null;
  isOpen: boolean;
  onClose: () => void;
}

export const FilePreviewModal = ({
  file,
  isOpen,
  onClose,
}: FilePreviewModalProps) => {
  const [fileUrl, setFileUrl] = useState<string>("");
  const [textContent, setTextContent] = useState<string>("");
  const [isTextFile, setIsTextFile] = useState<boolean>(false);
  const [isUnsupportedFormat, setIsUnsupportedFormat] =
    useState<boolean>(false);

  useEffect(() => {
    if (!file) return;

    // Criar URL para o arquivo
    const url = URL.createObjectURL(file);
    setFileUrl(url);

    // Verificar se é arquivo de texto (txt, json, xml, csv)
    const textTypes = [
      "text/plain",
      "application/json",
      "application/xml",
      "text/xml",
      "text/csv",
    ];

    if (textTypes.includes(file.type)) {
      setIsTextFile(true);
      // Ler conteúdo do arquivo
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setTextContent(content);
      };
      reader.readAsText(file);
    } else {
      setIsTextFile(false);
      setTextContent("");
    }

    // Verificar formatos não suportados (DOC, DOCX)
    const unsupportedTypes = [
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    setIsUnsupportedFormat(unsupportedTypes.includes(file.type));

    // Cleanup URL ao desmontar
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [file]);

  if (!file) return null;

  const handleDownload = () => {
    const a = document.createElement("a");
    a.href = fileUrl;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const renderPreview = () => {
    // Imagens
    if (file.type.startsWith("image/")) {
      return (
        <div className="flex justify-center items-center max-h-[70vh] overflow-auto bg-gray-50 rounded-lg p-4">
          <img
            src={fileUrl}
            alt={file.name}
            className="max-w-full max-h-full object-contain rounded"
          />
        </div>
      );
    }

    // Vídeos
    if (file.type.startsWith("video/")) {
      return (
        <div className="flex justify-center items-center bg-black rounded-lg">
          <video
            src={fileUrl}
            controls
            className="max-w-full max-h-[70vh] rounded"
          >
            Seu navegador não suporta reprodução de vídeo.
          </video>
        </div>
      );
    }

    // Áudio
    if (file.type.startsWith("audio/")) {
      return (
        <div className="flex flex-col items-center justify-center space-y-4 p-8">
          <div className="text-6xl mb-4">🎵</div>
          <div className="text-center">
            <p className="font-medium">{file.name}</p>
            <p className="text-sm text-muted-foreground">
              {(file.size / 1024).toFixed(1)} KB
            </p>
          </div>
          <audio src={fileUrl} controls className="w-full max-w-md">
            Seu navegador não suporta reprodução de áudio.
          </audio>
        </div>
      );
    }

    // PDF
    if (file.type === "application/pdf") {
      return (
        <div className="h-[70vh]">
          <iframe
            src={fileUrl}
            className="w-full h-full rounded-lg border"
            title={file.name}
          />
        </div>
      );
    }

    // Arquivos de texto (TXT, JSON, XML, CSV)
    if (isTextFile) {
      return (
        <div className="max-h-[70vh] overflow-auto bg-gray-50 rounded-lg p-4">
          <pre className="text-sm font-mono whitespace-pre-wrap break-words">
            {textContent}
          </pre>
        </div>
      );
    }

    // Formatos não suportados
    if (isUnsupportedFormat) {
      return (
        <Alert variant="destructive" className="my-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            O formato deste documento ({file.type.split("/")[1].toUpperCase()})
            não pode ser visualizado diretamente. Use o botão de download para
            baixar o arquivo.
          </AlertDescription>
        </Alert>
      );
    }

    // Fallback para tipos desconhecidos
    return (
      <Alert className="my-4">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Não é possível visualizar este tipo de arquivo. Use o botão de
          download para baixar o arquivo.
        </AlertDescription>
      </Alert>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span className="truncate mr-4">{file.name}</span>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownload}
                className="flex-shrink-0"
              >
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
            </div>
          </DialogTitle>
          <DialogDescription>
            Tipo: {file.type || "Desconhecido"} • Tamanho:{" "}
            {(file.size / 1024).toFixed(1)} KB
          </DialogDescription>
        </DialogHeader>
        <div className="mt-4">{renderPreview()}</div>
      </DialogContent>
    </Dialog>
  );
};

